import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { DepartmentDTOSchema, CreateDepartmentSchema, UpdateDepartmentSchema } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const DepartmentsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(DepartmentDTOSchema),
});

export function useDepartments(options: { enabled?: boolean } = {}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: "/api/v1/departments" });
      return DepartmentsResponseSchema.parse(json).data;
    },
    enabled: options.enabled !== false,
  });
}

export function useCreateDepartment() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateDepartmentSchema>) =>
      backend({ method: "POST", url: "/api/v1/departments", data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      toast.success(t("departments.created"));
    },
  });
}

export function useUpdateDepartment() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.infer<typeof UpdateDepartmentSchema> }) =>
      backend({ method: "PATCH", url: `/api/v1/departments/${id}`, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["departments"] });
      toast.success(t("departments.updated"));
    },
  });
}

export function useDeleteDepartment() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/departments/${id}` }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["departments"] }),
  });
}
